import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import CardContent from '@material-ui/core/CardContent';
import { withStyles } from '@material-ui/core/styles';
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import OpenInNew from "@material-ui/icons/OpenInNew";
import SketchOrderStatusChip from "../SketchOrderStatusChip/SketchOrderStatusChip";
import Badge from "@material-ui/core/Badge/Badge";
import { red } from '@material-ui/core/colors';
import CreateIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from "../IconButton";
import { SketchOrderStatus } from '../../lib/sketchOrderStatuses';
import getDisplayConfig from '../../lib/sketchOrderStatusDisplayConfig';

const style = (theme) => ({
    flex: {
        flexGrow: 1,
    },
    title: {
        color: '#FFFFFF',
        fontSize: 32,
        lineHeight: '32px',
        verticalAlign: 'middle',
    },
    subtitle: {
        color: '#FFFFFF',
    },
    iconButtons: {
        color: '#FFFFFF',
    }
});

const SketchOrderCardHeader = (props) => {
    const handleOpenDrawingClick = (showExp) => () => {
        if (props.project.hasDrawing) {
            const url = showExp
                ? `/orders/${props.sketchOrder.id}/sketch/${props.project.id}?showExp=true`
                : `/orders/${props.sketchOrder.id}/sketch/${props.project.id}`;
            props.history.push(url);
        } else {
            const url = showExp
                ? `/orders/${props.sketchOrder.id}/imagery?showExp=true`
                : `/orders/${props.sketchOrder.id}/imagery`;
            props.history.push(url);
        }
    };

    const isOpenDrawingButtonDisabled = () => {
        return (!props.project.id || props.project.isFetching || !props.authorizedToOpenDrawing || props.sketchOrder.sketchOrderStatus === SketchOrderStatus.Pending);
    };

    const isDeleteProjectButtonDisabled = () => {
        const deliveredStatuses = [SketchOrderStatus.Complete, SketchOrderStatus.Billed, SketchOrderStatus.BillingFailed];
        return (deliveredStatuses.includes(props.sketchOrder.sketchOrderStatus));
    };

    const handleViewProjectClick = () => {
        const impersonatorUserName = props.currentUserName.split('@')[0];
        window.open(`${process.env.REACT_APP_DECEPTIVE_DOLPHIN_HOST}login?returnurl=/projects/${props.sketchOrder.projectId}&username=${encodeURIComponent(`${props.sketchOrder.owner.userName}^${impersonatorUserName}`)}`, '_blank');
    };

    const sketchOrderStatusDisplayConfig = getDisplayConfig(props.sketchOrder.sketchOrderStatus);
    
    return (
        <CardContent style={{
            backgroundColor: sketchOrderStatusDisplayConfig.color,
            color: '#FFFFFF',
        }}>
            <Grid container spacing={1} alignItems="center">
                <Grid item>
                    {
                        sketchOrderStatusDisplayConfig.icon &&
                        React.cloneElement(sketchOrderStatusDisplayConfig.icon, { className: props.classes.title })
                    }
                </Grid>
                <Grid item className={props.classes.flex}>
                    <Typography className={props.classes.title}>
                        {sketchOrderStatusDisplayConfig.displayName}
                        {
                            props.isFetching &&
                            <CircularProgress />
                        }
                    </Typography>
                </Grid>
                <Grid item>
                    <IconButton
                        className={props.classes.iconButtons}
                        title="Open Drawing"
                        onClick={handleOpenDrawingClick(false)}
                        disabled={isOpenDrawingButtonDisabled()}
                        disabledTitle="Drawing Locked"
                    >
                        <CreateIcon />
                    </IconButton>
                    {
                        props.currentUserRoles.hasExpSketchToolEnabled &&
                        <IconButton
                            className={props.classes.iconButtons}
                            title="Open Drawing EXP"
                            onClick={handleOpenDrawingClick(true)}
                            disabled={isOpenDrawingButtonDisabled()}
                            disabledTitle="Drawing Locked"
                        >
                            <Badge badgeContent="EXP" color="error">
                                <CreateIcon />
                            </Badge>
                        </IconButton>
                    }
                    {
                        props.showViewProjectButton &&
                        <IconButton
                            className={props.classes.iconButtons}
                            title="View Project"
                            onClick={handleViewProjectClick}
                        >
                            <OpenInNew />
                        </IconButton>
                    }
                    {
                        props.authorizedToOpenDrawing &&
                        <IconButton
                            className={props.classes.iconButtons}
                            title="Delete Project"
                            onClick={props.onDeleteProjectButtonClick}
                            disabled={isDeleteProjectButtonDisabled()}
                            disabledTitle="Project Locked"
                        >
                            <DeleteIcon />
                        </IconButton>
                    }
                </Grid>
            </Grid>
            <Typography variant="subtitle1" className={props.classes.subtitle}>
                Order #{props.sketchOrder.id}
            </Typography>
        </CardContent>
    );
};

SketchOrderCardHeader.propTypes = {
    classes: PropTypes.shape({
        flex: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        subtitle: PropTypes.string.isRequired,
        iconButtons: PropTypes.string.isRequired,
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    sketchOrder: PropTypes.shape({
        id: PropTypes.number,
        sketchOrderStatus: PropTypes.string,
        projectId: PropTypes.string,
    }).isRequired,
    project: PropTypes.shape({
        id: PropTypes.string,
        hasDrawing: PropTypes.bool,
        isFetching: PropTypes.bool,
    }).isRequired,
    isFetching: PropTypes.bool.isRequired,
    showViewProjectButton: PropTypes.bool,
    authorizedToOpenDrawing: PropTypes.bool.isRequired,
    currentUserRoles: PropTypes.shape({
        hasExpSketchToolEnabled: PropTypes.bool
    }).isRequired,
    onDeleteProjectButtonClick: PropTypes.func.isRequired,
    currentUserName: PropTypes.string.isRequired,
};

SketchOrderCardHeader.defaultProps = {
    showViewProjectButton: false,
    project: {
        isFetching: true,
    },
};

export default withRouter(withStyles(style)(SketchOrderCardHeader));
